import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Typography,
  Chip,
  Snackbar,
  Alert,
  Divider,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { SAGE, CREAM, WARM } from '../theme';
import { API_URL } from '../config';
import { mockAuthHeaders } from '../utils/mockAuth';
import { uploadResumeForSignup } from '../services/signupService';

// Prototype route for the "journey" onboarding UX.
// Left (now) → middle (waypoints along the path) → right (destination mad-libs).
// Submit logs the would-be payload to console for now — no backend write.
// Promote to default flow once the feel is right.

const INTENT_OPTIONS = [
  { value: 'advance', verb: 'advance', label: 'Advance in my current field' },
  { value: 'pivot', verb: 'pivot to become', label: 'Pivot my career' },
  { value: 'new_position', verb: 'land a new position as', label: 'Find a new position' },
  { value: 'other', verb: 'explore what\'s possible as', label: 'Something else' },
];

const TIMELINE_OPTIONS = [
  { value: 'asap', label: 'ASAP', short: 'right now' },
  { value: '6_months', label: 'Within 6 months', short: 'in 6 months' },
  { value: '1_2_years', label: '1–2 years', short: 'in 1–2 years' },
  { value: 'exploring', label: 'Just exploring', short: 'eventually' },
];

const LEVEL_OPTIONS = [
  { value: null, label: 'any level' },
  { value: 'entry', label: 'Entry' },
  { value: 'mid', label: 'Mid-level' },
  { value: 'senior', label: 'Senior' },
  { value: 'staff', label: 'Staff' },
  { value: 'principal', label: 'Principal' },
  { value: 'manager', label: 'Manager' },
  { value: 'director', label: 'Director' },
];

const WAYPOINTS = [
  { key: 'intent', label: 'Intent' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'context', label: 'Context' },
];

const EMPTY = {
  name: '',
  preferred_name: '',
  intent: null,
  timeline: null,
  notes: '',
  target_role: null,   // {id, name, discipline_name}
  target_level: null,  // 'senior' | 'staff' | null
  target_market: null, // {id, name}
  signup_method: 'manual',
  resume_uploaded_at: null,
};

const SignupJourneyPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [taxonomy, setTaxonomy] = useState(null);
  const [marketsTax, setMarketsTax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resumeParsing, setResumeParsing] = useState(false);
  const [parsedSummary, setParsedSummary] = useState(null); // {title, company, yoe}
  const [snackbar, setSnackbar] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const [openWaypoint, setOpenWaypoint] = useState(null);  // 'intent' | 'timeline' | 'context' | null
  const [waypointAnchor, setWaypointAnchor] = useState(null);

  const [openMadLib, setOpenMadLib] = useState(null); // 'role' | 'level' | 'market' | null
  const [madLibAnchor, setMadLibAnchor] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/taxonomy`, { headers: { ...mockAuthHeaders() }, credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)),
      fetch(`${API_URL}/api/taxonomy/markets`, { headers: { ...mockAuthHeaders() }, credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([tax, markets]) => {
        setTaxonomy(tax);
        setMarketsTax(markets);
      })
      .catch((err) => console.error('Failed to load taxonomies:', err))
      .finally(() => setLoading(false));
  }, []);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const waypointState = (key) => {
    if (key === 'intent') return !!form.intent;
    if (key === 'timeline') return !!form.timeline;
    if (key === 'context') return (form.notes || '').trim().length > 0;
    return false;
  };
  const filledWaypoints = WAYPOINTS.filter((w) => waypointState(w.key)).length;

  const destinationReady = form.target_role && form.target_market;
  const requiredFilled = (
    form.name.trim().length > 0
    && form.intent
    && form.timeline
    && destinationReady
  );

  // -------- Resume upload --------
  const fileInputRef = useRef(null);
  const handleResumeUpload = async (file) => {
    if (!file) return;
    setResumeParsing(true);
    try {
      const result = await uploadResumeForSignup(file);
      const parsedName = result?.personal_info?.name || result?.name;
      const latestExp = (result?.experience && result.experience[0]) || null;
      setForm((prev) => ({
        ...prev,
        name: prev.name || parsedName || '',
        resume_uploaded_at: new Date().toISOString(),
        signup_method: 'resume',
      }));
      setParsedSummary(latestExp ? {
        title: latestExp.title,
        company: latestExp.company || latestExp.employer,
      } : null);
      setSnackbar({ severity: 'success', message: 'Resume parsed — we\'ll pull in the details on the left.' });
    } catch (err) {
      console.error('resume parse failed:', err);
      setSnackbar({ severity: 'error', message: 'Resume parse failed, you can fill in manually.' });
    } finally {
      setResumeParsing(false);
    }
  };

  // -------- Submit --------
  const handleSubmit = () => {
    const payload = {
      name: form.name.trim(),
      preferred_name: form.preferred_name?.trim() || null,
      signup_method: form.signup_method,
      intent: form.intent,
      intent_detail: null,
      timeline: form.timeline,
      markets: form.target_market ? [form.target_market.id] : [],
      notes: form.notes || null,
      product_updates_opt_in: false,
      resume_uploaded_at: form.resume_uploaded_at,
      // Prototype-only fields — not yet in backend schema
      _prototype_target_role: form.target_role,
      _prototype_target_level: form.target_level,
    };
    // eslint-disable-next-line no-console
    console.log('[SignupJourney] would submit:', payload);
    setSubmitted(true);
    setSnackbar({
      severity: 'info',
      message: 'Prototype — payload logged to console. Nothing sent to the backend yet.',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: SAGE[500] }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: CREAM[100], py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h3" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900] }}>
            Welcome to Onward
          </Typography>
          <Typography variant="body1" sx={{ color: SAGE[500], mt: 1 }}>
            Let's plot a journey from where you are to where you're going.
          </Typography>
          <Typography variant="caption" sx={{ color: SAGE[300], display: 'block', mt: 0.5 }}>
            {filledWaypoints} of {WAYPOINTS.length} waypoints marked
          </Typography>
        </Box>

        {/* The journey: three columns — now / path / destination */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.3fr 1fr',
          gap: 2,
          alignItems: 'stretch',
          minHeight: 420,
        }}>
          {/* LEFT: Where are you now */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5, fontSize: '1.1rem' }}>
              Where are you now?
            </Typography>
            <Typography variant="caption" sx={{ color: SAGE[400], mb: 2 }}>
              The starting point.
            </Typography>

            <Stack spacing={2} sx={{ flex: 1 }}>
              <TextField
                label="Your name"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="Preferred name (optional)"
                value={form.preferred_name}
                onChange={(e) => update({ preferred_name: e.target.value })}
                size="small"
                fullWidth
              />

              <Divider sx={{ my: 1 }} />

              {parsedSummary ? (
                <Box sx={{ p: 2, bgcolor: SAGE[50], borderRadius: 2, border: `1px solid ${SAGE[200]}` }}>
                  <Typography variant="caption" sx={{ color: SAGE[400], fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    From your resume
                  </Typography>
                  <Typography sx={{ color: SAGE[800], fontWeight: 600, mt: 0.5 }}>
                    {parsedSummary.title || 'Your current role'}
                  </Typography>
                  {parsedSummary.company && (
                    <Typography variant="body2" sx={{ color: SAGE[500] }}>
                      at {parsedSummary.company}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={resumeParsing}
                    sx={{ mt: 1, color: SAGE[500], textTransform: 'none', p: 0, minWidth: 0 }}
                  >
                    Replace resume
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={resumeParsing ? <CircularProgress size={16} /> : <UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={resumeParsing}
                  sx={{
                    textTransform: 'none',
                    borderColor: SAGE[300],
                    color: SAGE[700],
                    py: 1.5,
                    '&:hover': { borderColor: SAGE[500], bgcolor: SAGE[50] },
                  }}
                >
                  {resumeParsing ? 'Parsing…' : 'Upload resume to autofill'}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.txt"
                style={{ display: 'none' }}
                onChange={(e) => handleResumeUpload(e.target.files?.[0])}
              />
            </Stack>
          </Paper>

          {/* MIDDLE: The journey path */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', py: 2 }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[500], mb: 3, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Let's talk about the journey
            </Typography>

            <JourneyPath
              waypoints={WAYPOINTS}
              filled={WAYPOINTS.map((w) => waypointState(w.key))}
              onClickDot={(key, anchor) => {
                setOpenWaypoint(key);
                setWaypointAnchor(anchor);
              }}
              form={form}
            />

            <Typography variant="caption" sx={{ color: SAGE[400], mt: 3, textAlign: 'center', maxWidth: 260 }}>
              Click any dot to mark the waypoint. The line solidifies as the picture clears.
            </Typography>
          </Box>

          {/* RIGHT: Where are you going (mad-libs) */}
          <Paper elevation={2} sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', bgcolor: CREAM[50] }}>
            <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700, color: SAGE[900], mb: 0.5, fontSize: '1.1rem' }}>
              Where are you going?
            </Typography>
            <Typography variant="caption" sx={{ color: SAGE[400], mb: 3 }}>
              The destination.
            </Typography>

            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <MadLibSentence
                form={form}
                taxonomy={taxonomy}
                marketsTax={marketsTax}
                onOpen={(which, anchor) => {
                  setOpenMadLib(which);
                  setMadLibAnchor(anchor);
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Submit — only visible when required fields filled */}
        <Box sx={{ mt: 5, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled={!requiredFilled || submitted}
            onClick={handleSubmit}
            startIcon={submitted ? <CheckIcon /> : null}
            sx={{
              bgcolor: SAGE[700],
              textTransform: 'none',
              fontFamily: "'Outfit', sans-serif",
              fontSize: '1rem',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': { bgcolor: SAGE[800] },
            }}
          >
            {submitted ? 'Logged to console' : 'Begin the journey'}
          </Button>
          {!requiredFilled && (
            <Typography variant="caption" sx={{ color: SAGE[400], display: 'block', mt: 1 }}>
              Fill your name, all three waypoints, and the destination to continue.
            </Typography>
          )}
        </Box>
      </Container>

      {/* Waypoint popovers */}
      <Popover
        open={openWaypoint === 'intent'}
        anchorEl={waypointAnchor}
        onClose={() => setOpenWaypoint(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 3, width: 340 }}>
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, mb: 1.5 }}>
            What kind of move is this?
          </Typography>
          <Stack spacing={1}>
            {INTENT_OPTIONS.map((opt) => (
              <ChoiceRow
                key={opt.value}
                label={opt.label}
                selected={form.intent === opt.value}
                onClick={() => {
                  update({ intent: opt.value });
                  setOpenWaypoint(null);
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>

      <Popover
        open={openWaypoint === 'timeline'}
        anchorEl={waypointAnchor}
        onClose={() => setOpenWaypoint(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 3, width: 280 }}>
          <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, mb: 1.5 }}>
            What's your timeline?
          </Typography>
          <Stack spacing={1}>
            {TIMELINE_OPTIONS.map((opt) => (
              <ChoiceRow
                key={opt.value}
                label={opt.label}
                selected={form.timeline === opt.value}
                onClick={() => {
                  update({ timeline: opt.value });
                  setOpenWaypoint(null);
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>

      <Dialog open={openWaypoint === 'context'} onClose={() => setOpenWaypoint(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Tell us about the journey</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: SAGE[500], mb: 2 }}>
            What's driving this move? What's been hardest? Anything you'd want us to know?
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={form.notes}
            onChange={(e) => update({ notes: e.target.value })}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWaypoint(null)} sx={{ textTransform: 'none' }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Mad-libs popovers */}
      <RolePopover
        open={openMadLib === 'role'}
        anchorEl={madLibAnchor}
        onClose={() => setOpenMadLib(null)}
        taxonomy={taxonomy}
        selected={form.target_role}
        onSelect={(role) => {
          update({ target_role: role });
          setOpenMadLib(null);
        }}
      />

      <Popover
        open={openMadLib === 'level'}
        anchorEl={madLibAnchor}
        onClose={() => setOpenMadLib(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 220 }}>
          <Stack spacing={0.5}>
            {LEVEL_OPTIONS.map((opt) => (
              <ChoiceRow
                key={opt.label}
                label={opt.label}
                selected={form.target_level === opt.value}
                onClick={() => {
                  update({ target_level: opt.value });
                  setOpenMadLib(null);
                }}
              />
            ))}
          </Stack>
        </Box>
      </Popover>

      <MarketPopover
        open={openMadLib === 'market'}
        anchorEl={madLibAnchor}
        onClose={() => setOpenMadLib(null)}
        marketsTax={marketsTax}
        selected={form.target_market}
        onSelect={(market) => {
          update({ target_market: market });
          setOpenMadLib(null);
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar && <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity}>{snackbar.message}</Alert>}
      </Snackbar>
    </Box>
  );
};

// ============================================================================
// Journey path — SVG with 3 waypoint dots
// ============================================================================

const JourneyPath = ({ waypoints, filled, onClickDot, form }) => {
  // Horizontal track with equally spaced dots. Height is decorative.
  const W = 340;
  const H = 240;
  const trackY = H / 2;
  const padX = 20;
  const dotXs = waypoints.map((_, i) => padX + ((W - 2 * padX) / (waypoints.length - 1)) * i);

  const segments = [];
  for (let i = 0; i < dotXs.length - 1; i++) {
    segments.push({
      x1: dotXs[i],
      x2: dotXs[i + 1],
      solid: filled[i] && filled[i + 1],
    });
  }

  const getLabel = (key) => {
    if (key === 'intent' && form.intent) return INTENT_OPTIONS.find((o) => o.value === form.intent)?.label;
    if (key === 'timeline' && form.timeline) return TIMELINE_OPTIONS.find((o) => o.value === form.timeline)?.label;
    if (key === 'context' && (form.notes || '').trim()) return 'Journey notes saved';
    return null;
  };

  return (
    <Box sx={{ width: W, position: 'relative' }}>
      <svg width={W} height={H} style={{ display: 'block' }}>
        {/* Leading dashed entry segment from left card */}
        <line x1={0} y1={trackY} x2={dotXs[0]} y2={trackY}
          stroke={SAGE[300]} strokeWidth={2} strokeDasharray="4 4" />
        {/* Between-waypoint segments */}
        {segments.map((seg, i) => (
          <line
            key={i}
            x1={seg.x1} y1={trackY} x2={seg.x2} y2={trackY}
            stroke={seg.solid ? SAGE[600] : SAGE[300]}
            strokeWidth={seg.solid ? 3 : 2}
            strokeDasharray={seg.solid ? '0' : '4 4'}
          />
        ))}
        {/* Trailing segment to right card */}
        <line x1={dotXs[dotXs.length - 1]} y1={trackY} x2={W} y2={trackY}
          stroke={filled[filled.length - 1] ? SAGE[600] : SAGE[300]}
          strokeWidth={filled[filled.length - 1] ? 3 : 2}
          strokeDasharray={filled[filled.length - 1] ? '0' : '4 4'} />
      </svg>

      {/* Dots rendered as absolutely-positioned buttons so they're easily clickable */}
      {waypoints.map((wp, i) => {
        const isFilled = filled[i];
        const label = getLabel(wp.key);
        return (
          <Box
            key={wp.key}
            sx={{
              position: 'absolute',
              top: trackY,
              left: dotXs[i],
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => onClickDot(wp.key, e.currentTarget)}
              sx={{
                width: 26,
                height: 26,
                bgcolor: isFilled ? SAGE[700] : '#fff',
                border: `2px solid ${isFilled ? SAGE[700] : SAGE[400]}`,
                borderStyle: isFilled ? 'solid' : 'dashed',
                '&:hover': { bgcolor: isFilled ? SAGE[800] : SAGE[50] },
              }}
            >
              {isFilled ? (
                <CheckIcon sx={{ fontSize: 14, color: '#fff' }} />
              ) : null}
            </IconButton>
            <Typography variant="caption" sx={{
              color: isFilled ? SAGE[700] : SAGE[400],
              fontWeight: isFilled ? 600 : 400,
              position: 'absolute',
              top: 32,
              whiteSpace: 'nowrap',
              fontFamily: "'Outfit', sans-serif",
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}>
              {wp.label}
            </Typography>
            {label && (
              <Typography sx={{
                position: 'absolute',
                top: -30,
                color: SAGE[700],
                fontSize: '0.75rem',
                fontFamily: "'Outfit', sans-serif",
                whiteSpace: 'nowrap',
                maxWidth: 120,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                textAlign: 'center',
              }}>
                {label}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

// ============================================================================
// Mad-libs sentence — right card
// ============================================================================

const MadLibSentence = ({ form, taxonomy, marketsTax, onOpen }) => {
  const intentOption = INTENT_OPTIONS.find((o) => o.value === form.intent);
  const verb = intentOption?.verb || null;
  const levelOption = LEVEL_OPTIONS.find((o) => o.value === form.target_level);
  const levelLabel = form.target_level ? levelOption?.label : null;
  const roleLabel = form.target_role?.name;
  const marketLabel = form.target_market?.name;
  const timelineOption = TIMELINE_OPTIONS.find((o) => o.value === form.timeline);
  const timelineShort = timelineOption?.short;

  return (
    <Box sx={{ fontFamily: "'Outfit', sans-serif", fontSize: '1.25rem', lineHeight: 1.9, color: SAGE[800] }}>
      I want to{' '}
      <InlineVerb text={verb || '____'} faded={!verb} />
      {' '}as a{' '}
      <MadLibBlank
        label={levelLabel || 'any level'}
        placeholder="any level"
        filled={form.target_level !== null}
        onClick={(e) => onOpen('level', e.currentTarget)}
      />{' '}
      <MadLibBlank
        label={roleLabel || 'role'}
        placeholder="role"
        filled={!!roleLabel}
        onClick={(e) => onOpen('role', e.currentTarget)}
        emphasized
      />{' '}
      in{' '}
      <MadLibBlank
        label={marketLabel || 'market'}
        placeholder="market"
        filled={!!marketLabel}
        onClick={(e) => onOpen('market', e.currentTarget)}
        emphasized
      />
      {timelineShort && (
        <>
          {', '}
          <span style={{ color: SAGE[500] }}>{timelineShort}</span>
        </>
      )}
      {'.'}
    </Box>
  );
};

const InlineVerb = ({ text, faded }) => (
  <span style={{
    color: faded ? SAGE[300] : SAGE[700],
    fontWeight: faded ? 400 : 600,
    fontStyle: faded ? 'italic' : 'normal',
  }}>
    {text}
  </span>
);

const MadLibBlank = ({ label, placeholder, filled, onClick, emphasized }) => (
  <Box
    component="span"
    onClick={onClick}
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.25,
      mx: 0.25,
      borderRadius: 1,
      cursor: 'pointer',
      bgcolor: filled ? (emphasized ? SAGE[100] : 'transparent') : CREAM[200],
      borderBottom: filled ? `2px solid ${SAGE[500]}` : `2px dashed ${SAGE[300]}`,
      color: filled ? SAGE[800] : SAGE[400],
      fontWeight: filled ? 600 : 400,
      fontStyle: filled ? 'normal' : 'italic',
      transition: 'all 0.15s',
      '&:hover': { bgcolor: filled ? SAGE[200] : CREAM[300] },
    }}
  >
    {filled ? label : `[${placeholder}]`}
    {filled && <EditIcon sx={{ fontSize: 12, ml: 0.5, color: SAGE[400] }} />}
  </Box>
);

// ============================================================================
// Pickers — role (discipline → role) and market (region grouping)
// ============================================================================

const RolePopover = ({ open, anchorEl, onClose, taxonomy, selected, onSelect }) => {
  const [expandedDisc, setExpandedDisc] = useState(null);

  if (!taxonomy) return null;
  const disciplines = taxonomy.disciplines || [];
  const roles = taxonomy.roles || [];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Box sx={{ p: 2, width: 380, maxHeight: 420, overflow: 'auto' }}>
        <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, mb: 1.5 }}>
          Pick a role
        </Typography>
        <Stack spacing={0.5}>
          {disciplines.map((disc) => {
            const discRoles = roles.filter((r) => r.discipline_id === disc.id);
            const isOpen = expandedDisc === disc.id;
            return (
              <Box key={disc.id}>
                <Box
                  onClick={() => setExpandedDisc(isOpen ? null : disc.id)}
                  sx={{
                    p: 1, cursor: 'pointer', borderRadius: 1,
                    fontFamily: "'Outfit', sans-serif", fontWeight: 600,
                    color: SAGE[700], fontSize: '0.85rem',
                    '&:hover': { bgcolor: CREAM[200] },
                    display: 'flex', justifyContent: 'space-between',
                  }}
                >
                  <span>{disc.name}</span>
                  <span style={{ color: SAGE[400] }}>{discRoles.length}</span>
                </Box>
                {isOpen && (
                  <Stack sx={{ pl: 2 }} spacing={0.25}>
                    {discRoles.map((r) => (
                      <Box
                        key={r.id}
                        onClick={() => onSelect({ id: r.id, name: r.name, discipline_name: disc.name })}
                        sx={{
                          p: 0.75, px: 1, cursor: 'pointer', borderRadius: 1,
                          bgcolor: selected?.id === r.id ? SAGE[100] : 'transparent',
                          color: SAGE[700], fontSize: '0.85rem',
                          fontFamily: "'Outfit', sans-serif",
                          '&:hover': { bgcolor: SAGE[50] },
                        }}
                      >
                        {r.name}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Popover>
  );
};

const MarketPopover = ({ open, anchorEl, onClose, marketsTax, selected, onSelect }) => {
  if (!marketsTax) return null;
  const markets = marketsTax.markets || [];
  const groups = [
    { label: 'Remote', items: markets.filter((m) => m.type === 'remote') },
    { label: 'US Metros', items: markets.filter((m) => m.type === 'metro' && m.region_id === 'us') },
    { label: 'Canadian Metros', items: markets.filter((m) => m.type === 'metro' && m.region_id === 'ca') },
  ];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Box sx={{ p: 2, width: 360, maxHeight: 420, overflow: 'auto' }}>
        <Typography sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, mb: 1.5 }}>
          Pick a market
        </Typography>
        <Stack spacing={2}>
          {groups.filter((g) => g.items.length > 0).map((g) => (
            <Box key={g.label}>
              <Typography sx={{ fontSize: '0.7rem', color: SAGE[400], mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'Outfit', sans-serif" }}>
                {g.label}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {g.items.map((m) => (
                  <Chip
                    key={m.id}
                    label={m.name + (m.served ? ' ★' : '')}
                    size="small"
                    onClick={() => onSelect({ id: m.id, name: m.name })}
                    sx={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: '0.78rem',
                      cursor: 'pointer',
                      bgcolor: selected?.id === m.id ? SAGE[700] : CREAM[200],
                      color: selected?.id === m.id ? '#fff' : SAGE[700],
                      '&:hover': {
                        bgcolor: selected?.id === m.id ? SAGE[800] : SAGE[100],
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Popover>
  );
};

// ============================================================================
// Shared bits
// ============================================================================

const ChoiceRow = ({ label, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      p: 1.25, borderRadius: 1.5, cursor: 'pointer',
      border: `1.5px solid ${selected ? SAGE[600] : SAGE[200]}`,
      bgcolor: selected ? SAGE[50] : 'transparent',
      '&:hover': { bgcolor: CREAM[100], borderColor: SAGE[400] },
    }}
  >
    <Typography sx={{
      fontFamily: "'Outfit', sans-serif",
      color: selected ? SAGE[800] : SAGE[700],
      fontWeight: selected ? 600 : 400,
      fontSize: '0.88rem',
    }}>
      {label}
    </Typography>
  </Box>
);

export default SignupJourneyPage;
